/**
 * MonthlyPaymentMatcher — compliant "estimate my monthly payment" widget.
 * We do NOT show treatment prices (practice policy). Instead it asks 2 quick
 * qualifying questions, then captures a lead that routes to the FREE consult
 * where the patient gets their exact personalized monthly payment.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEETH = [
  { v: 'a single tooth', label: 'One tooth' },
  { v: 'a few teeth', label: 'A few teeth' },
  { v: 'a full arch (All-on-4)', label: 'Full arch / All-on-4' },
];
const INSURANCE = [
  { v: 'has dental insurance', label: 'Yes, I have insurance' },
  { v: 'has no dental insurance', label: 'No insurance' },
];

const isPhone = (p) => (p || '').replace(/\D/g, '').length >= 10;

const MonthlyPaymentMatcher = () => {
  const [step, setStep] = useState(1);
  const [teeth, setTeeth] = useState('');
  const [ins, setIns] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim() || !isPhone(phone) || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErr('Please enter your name, a valid phone number, and email.');
      return;
    }
    setLoading(true);
    setErr('');
    try {
      await axios.post(`${API}/appointments`, {
        name: name.trim(),
        phone: phone.replace(/\D/g, ''),
        email: email.trim(),
        source: 'affordable_implants_estimator',
        referral_source: 'Search Engine',
        preferred_day: 'Monday',
        preferred_time: 'Morning',
        patient_type: 'new',
        contact_preference: 'call',
        message: `Payment options request — replacing ${teeth || 'unspecified'}, ${ins || 'insurance not specified'}. Wants a monthly payment quote at free consult.`,
      });
      setDone(true);
    } catch (e) {
      setErr('Something went wrong. Please call (817) 985-3337.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border-l-4 border-teal-500 p-8 text-center" data-testid="matcher-success">
        <div className="text-4xl mb-3" aria-hidden="true">✅</div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">You're likely a great candidate for low monthly payments!</h3>
        <p className="text-gray-600 mb-6">Our treatment coordinator will call you to walk through your 0% financing options and set up your FREE consultation, where you'll get your exact monthly payment — no surprises.</p>
        <a href="tel:8179853337" data-testid="matcher-call" className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-bold px-8 py-4 rounded-lg">
          Prefer to talk now? Call (817) 985-3337
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border-l-4 border-teal-500 p-8" data-testid="payment-matcher">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl" aria-hidden="true">💳</span>
        <h3 className="text-2xl font-bold text-gray-900">Estimate My Monthly Payment</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">2 quick questions — no cost, no obligation. Step {step} of 3.</p>

      {step === 1 && (
        <div data-testid="matcher-step-1">
          <p className="font-semibold text-gray-800 mb-3">What are you looking to replace?</p>
          <div className="grid gap-3">
            {TEETH.map((t) => (
              <button key={t.v} data-testid={`teeth-${t.label}`} onClick={() => { setTeeth(t.v); setStep(2); }}
                className="text-left border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 rounded-lg px-5 py-3 font-medium text-gray-800 transition-colors">
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="matcher-step-2">
          <p className="font-semibold text-gray-800 mb-3">Do you have dental insurance?</p>
          <div className="grid gap-3">
            {INSURANCE.map((i) => (
              <button key={i.v} data-testid={`ins-${i.label}`} onClick={() => { setIns(i.v); setStep(3); }}
                className="text-left border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 rounded-lg px-5 py-3 font-medium text-gray-800 transition-colors">
                {i.label}
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} className="mt-4 text-sm text-teal-600 hover:underline">← Back</button>
        </div>
      )}

      {step === 3 && (
        <div data-testid="matcher-step-3">
          <p className="font-semibold text-gray-800 mb-1">Almost done!</p>
          <p className="text-sm text-gray-500 mb-4">Where should we send your personalized payment options?</p>
          <div className="space-y-3">
            <Input data-testid="matcher-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={100} />
            <Input data-testid="matcher-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s\-()]/g, ''))} placeholder="(817) 555-1234" maxLength={20} />
            <Input data-testid="matcher-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" maxLength={100} />
            {err && <p className="text-red-500 text-sm">{err}</p>}
            <Button data-testid="matcher-submit" onClick={submit} disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 font-bold">
              {loading ? 'Sending…' : 'See My Payment Options →'}
            </Button>
          </div>
          <button onClick={() => setStep(2)} className="mt-4 text-sm text-teal-600 hover:underline">← Back</button>
          <p className="text-xs text-gray-400 mt-3">We never show generic prices — every quote is personalized at your free consult. Your info is secure.</p>
        </div>
      )}
    </div>
  );
};

export default MonthlyPaymentMatcher;
